import React from "react";
import { TanstackSortingFn } from "../../../../utils/TableSorting";
import { FaUserShield } from "react-icons/fa";
import { HiPencil, HiLockOpen } from "react-icons/hi2";
import { Avatar } from "@mantine/core";
import { CustomSwitch } from "../../../ui/CustomSwitch/CustomSwitch";
import BaseTable from "../../../ui/CustomTanStackTable/CustomTanStackTable";
import TableRowActionsMenu from "../../../ui/TableRowActionsMenu/TableRowActionsMenu";
import { getDisplayValue } from "../../../../utils/transactionutils";
import ColumnVisibilitySelector from "../../../ui/ColumnVisibilitySelector/ColumnVisibilitySelector";
import useColumnVisibilityStore, {
  selectVisibleColumns,
} from "../../../../stores/columnVisibilityStore";

const DEFAULT_COLUMN_KEYS = ["name", "email"];
const OPTIONAL_COLUMN_KEYS = ["role", "accountLocked"];

const COLUMN_META = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "role", label: "Role" },
  { key: "accountLocked", label: "Status" },
];

function UserRoleTableTanstack({
  data,
  onEdit,
  loadingIds,
  onToggleStatus,
  onManageRoles,
  onUnblockPin,
  isRefetching = false,
  isSuperAdmin = false,
}) {
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const visibleOptionalKeys = useColumnVisibilityStore(
    selectVisibleColumns("rbac-users"),
  );
  const setVisibleOptionalKeys = useColumnVisibilityStore(
    (s) => s.setVisibleColumns,
  );

  const columnVisibility = React.useMemo(() => {
    const visibility = {};
    OPTIONAL_COLUMN_KEYS.forEach((key) => {
      visibility[key] = visibleOptionalKeys.includes(key);
    });
    return visibility;
  }, [visibleOptionalKeys]);

  const columns = React.useMemo(
    () => [
      {
        header: "Name",
        accessorKey: "name",
        cell: (info) => {
          const user = info.row.original;
          const name = info.getValue();
          const initials =
            user.firstname && user.lastname
              ? `${user.firstname[0]}${user.lastname[0]}`.toUpperCase()
              : name?.substring(0, 2).toUpperCase() || "??";
          const isActive = !user.accountLocked;
          const avatarColor = isActive ? "success" : "primary";

          return (
            <div className="flex items-center gap-3">
              <Avatar color={avatarColor} radius="xl" size="sm">
                {initials}
              </Avatar>
              <span
                className="max-w-[120px] truncate"
                title={getDisplayValue(name)}
              >
                {getDisplayValue(name)}
              </span>
            </div>
          );
        },
        sortingFn: TanstackSortingFn,
      },
      {
        header: "Email",
        accessorKey: "email",
        cell: (info) => (
          <div
            className="max-w-[180px] truncate"
            title={getDisplayValue(info.getValue())}
          >
            {getDisplayValue(info.getValue())}
          </div>
        ),
        sortingFn: TanstackSortingFn,
      },
      {
        header: "Role",
        accessorKey: "role",
        cell: (info) => {
          const role = info.getValue();
          const displayRole = role === "SUPER_ADMIN" ? "SYSTEM_ADMIN" : role;
          return (
            <div
              className="max-w-[120px] truncate"
              title={getDisplayValue(displayRole)}
            >
              {getDisplayValue(displayRole)}
            </div>
          );
        },
        sortingFn: TanstackSortingFn,
      },
      {
        header: "Status",
        accessorKey: "accountLocked",
        cell: ({ row }) => {
          const user = row.original;
          const isActive = !user.accountLocked;
          const isLoading = loadingIds.has(user.id);

          return (
            <CustomSwitch
              checked={isActive}
              onChange={() => onToggleStatus(user)}
              disabled={isLoading}
              color="primary"
              activeLabel="Activated"
              inactiveLabel="Deactivated"
            />
          );
        },
        enableSorting: false,
      },
      {
        id: "actions",
        header: () => <div className="text-right">Actions</div>,
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          return (
            <TableRowActionsMenu
              items={[
                {
                  label: "Edit User",
                  icon: <HiPencil size={14} />,
                  onClick: () => onEdit?.(row),
                },
                ...(isSuperAdmin
                  ? [
                      {
                        label: "Manage Roles",
                        icon: <FaUserShield size={14} />,
                        onClick: () => onManageRoles?.(row),
                      },
                    ]
                  : []),
                ...(isSuperAdmin && row.pinLocked
                  ? [
                      {
                        label: isRefetching
                          ? "Refreshing user status..."
                          : "Unblock PIN",
                        icon: <HiLockOpen size={14} />,
                        onClick: () => onUnblockPin?.(row),
                        disabled: isRefetching,
                        disabledReason: isRefetching
                          ? "Refreshing user status..."
                          : undefined,
                      },
                    ]
                  : []),
              ]}
            />
          );
        },
      },
    ],
    [
      onEdit,
      onToggleStatus,
      loadingIds,
      onManageRoles,
      onUnblockPin,
      isRefetching,
      isSuperAdmin,
    ],
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end mt-2">
        <ColumnVisibilitySelector
          columns={COLUMN_META}
          defaultColumnKeys={DEFAULT_COLUMN_KEYS}
          visibleColumnKeys={visibleOptionalKeys}
          onChange={(keys) => setVisibleOptionalKeys("rbac-users", keys)}
        />
      </div>
      <BaseTable
        columns={columns}
        data={data}
        columnVisibility={columnVisibility}
        pageSize={pagination.pageSize}
        pagination={pagination}
        onPaginationChange={setPagination}
        paginationMode="client"
        emptyMessage="No Users Found"
        rowClassName={() =>
          "hover:bg-blue-50 cursor-pointer bg-white-50 h-[50px] "
        }
      />
    </div>
  );
}

export default UserRoleTableTanstack;
